import { UploadZone } from "@components:UploadZone";
import { Box, Tab, Tabs, TextField, Typography } from "@mui/material";
import { useState } from "react";
import { Control, Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { MdLink, MdUploadFile } from "react-icons/md";

import { DEFAULT_BROWSER_TITLE, DEFAULT_FAVICON_URL } from "./constants";
import { FieldLabel } from "./shared-styles";

interface BrowserSettingsSectionProps {
  faviconPreview: string | null;
  onFaviconUpload: (file: File) => void;
  onFaviconRemove: () => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: Control<any>;
  faviconUrl: string;
  onFaviconUrlClear: () => void;
}

export const BrowserSettingsSection = ({
  faviconPreview,
  onFaviconUpload,
  onFaviconRemove,
  control,
  faviconUrl,
  onFaviconUrlClear,
}: BrowserSettingsSectionProps) => {
  const { t } = useTranslation();
  const [faviconMode, setFaviconMode] = useState<"url" | "upload">(
    faviconUrl ? "url" : "upload",
  );

  const handleModeChange = (
    _: React.SyntheticEvent,
    newMode: "url" | "upload",
  ) => {
    setFaviconMode(newMode);
    // Clear the other mode's value when switching
    if (newMode === "url") {
      onFaviconRemove();
    } else {
      onFaviconUrlClear();
    }
  };

  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 2,
        bgcolor: "grey.50",
        border: "1px solid",
        borderColor: "grey.200",
      }}
    >
      <Typography
        variant="subtitle2"
        sx={{
          fontWeight: 600,
          mb: 2,
          color: "text.primary",
          fontSize: "0.8rem",
        }}
      >
        {t("preferences.uiBranding.browserSettings")}
      </Typography>

      {/* Browser Title */}
      <Box sx={{ mb: 2.5 }}>
        <FieldLabel>{t("preferences.uiBranding.browserTabTitle")}</FieldLabel>
        <Controller
          name="browserTitle"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              value={field.value ?? ""}
              onChange={(e) => field.onChange(e.target.value || "")}
              fullWidth
              size="small"
              placeholder={DEFAULT_BROWSER_TITLE}
              sx={{
                "& .MuiOutlinedInput-root": { bgcolor: "common.white" },
                "& .MuiOutlinedInput-input": {
                  py: 1,
                  px: 1.25,
                  fontSize: "0.8rem",
                },
              }}
            />
          )}
        />
        <Typography
          variant="caption"
          sx={{ color: "text.secondary", mt: 0.5, display: "block" }}
        >
          {t("preferences.uiBranding.browserTabTitleHint")}
        </Typography>
      </Box>

      {/* Favicon */}
      <Box>
        <FieldLabel>{t("preferences.uiBranding.favicon")}</FieldLabel>

        {/* Mode Tabs */}
        <Tabs
          value={faviconMode}
          onChange={handleModeChange}
          sx={{
            minHeight: 32,
            mb: 1.5,
            "& .MuiTab-root": {
              minHeight: 32,
              py: 0.5,
              px: 1.5,
              fontSize: "0.7rem",
              textTransform: "none",
            },
          }}
        >
          <Tab
            value="url"
            label={t("preferences.uiBranding.url")}
            icon={<MdLink size={14} />}
            iconPosition="start"
          />
          <Tab
            value="upload"
            label={t("common.upload")}
            icon={<MdUploadFile size={14} />}
            iconPosition="start"
          />
        </Tabs>

        {faviconMode === "url" ? (
          <Controller
            name="faviconUrl"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                value={field.value ?? ""}
                onChange={(e) => field.onChange(e.target.value || "")}
                fullWidth
                size="small"
                placeholder={DEFAULT_FAVICON_URL}
                sx={{
                  "& .MuiOutlinedInput-root": { bgcolor: "common.white" },
                  "& .MuiOutlinedInput-input": {
                    py: 1,
                    px: 1.25,
                    fontSize: "0.8rem",
                  },
                }}
              />
            )}
          />
        ) : (
          <UploadZone
            size="compact"
            onFileSelect={onFaviconUpload}
            selectedFile={null}
            previewUrl={faviconPreview}
            accept="image/png,image/jpeg,image/svg+xml,image/x-icon,image/ico,image/webp"
            title={t("preferences.uiBranding.faviconDropTitle")}
            hint={t("preferences.uiBranding.faviconHint")}
          />
        )}

        <Typography
          variant="caption"
          sx={{ color: "text.secondary", mt: 0.5, display: "block" }}
        >
          {t("preferences.uiBranding.faviconHintCaption")}
        </Typography>
      </Box>
    </Box>
  );
};
